import type { TextOnPathConfig } from '@/lib/shapes/types/text-on-path-config'
import { resolveTextOnPathTypography } from '@/lib/shapes/text-on-path-session'
import { TEXT_ON_PATH_TEXT_NODE_ATTR } from '@/lib/shapes/text-on-path-characters'
import { useFontsOptional } from '@/providers/font'

interface TextOnPathTextProps {
  config: TextOnPathConfig
  /**
   * Only ever used to stamp `TEXT_ON_PATH_TEXT_NODE_ATTR` below, so the save path can find this
   * exact node. The per-character `<PX>` geometry the wire needs is readable nowhere else — see
   * `lib/shapes/text-on-path-characters.ts`.
   */
  shapeId: string
  pathId: string
  startOffset: number
  /**
   * Perpendicular offset to draw with, which is NOT always `config.offset`: a counter-clockwise
   * OPEN path expresses its flip by mirroring the side the text rides on
   * (`resolveTextOnPathDirection`). Passed in rather than re-derived so there is exactly one place
   * that decides it.
   */
  offset: number
}

/**
 * The text itself, drawn along `pathId` (WMPCM-9514 Story 1, styled in WMPCM-9522 Story 2).
 *
 * A separate component purely to own the font-catalog subscription. The renderer draws a shape's
 * COMMITTED `textOnPath` as well as the live draft, so the family/variant lookup has to happen
 * here rather than being resolved up in `center-canvas/center-canvas.tsx` and passed down — that would leave
 * every non-editing shape unstyled. Subscribing in this child instead of in `ShapeRenderer` keeps
 * the context out of every shape in the spread: only a shape that actually carries text-on-path
 * re-renders when `facesLoadedGeneration` bumps.
 *
 * That generation bump is load-bearing: `getFamilyCssName` starts returning the real family name
 * one render BEFORE the woff has been fetched, so without depending on the whole context value the
 * text would paint in the `sans-serif` fallback and nothing would invalidate it.
 */
export function TextOnPathText({
  config,
  shapeId,
  pathId,
  startOffset,
  offset,
}: TextOnPathTextProps) {
  // `useFontsOptional`, not `useFonts`: this renderer is mounted in tests and previews with no
  // FontProvider above it, and the throwing variant would take the whole shape down. The resolver
  // already falls back to a generic family when the lookup is absent.
  const fonts = useFontsOptional()
  const { fontFamily, fontWeight, fontStyle, fontSize, letterSpacing, fill } =
    resolveTextOnPathTypography(config, fonts ?? undefined)

  return (
    <text
      fontSize={fontSize}
      fill={fill}
      className="pointer-events-none"
      /*
        Without this SVG collapses every run of whitespace to a single space and strips the
        leading and trailing ones, so text typed as `Hi__________Helly` draws as `Hi Helly`.
        Measured in Chrome: a 17-character string reports `getNumberOfChars() === 8` by default,
        and 17 with this set (WMPCM, "spacing entered ... not applied to the shape border").

        That character count is not only a rendering concern. The wire format stores one `<PX>
        entry per character alongside the whole string in `<TL>`, and the per-character geometry
        can only be read back out of the laid-out DOM — so a collapsed run would write 8 entries
        for a 17-character line and put the two halves permanently out of step (WMPCM-9542).

        `xml:space` rather than CSS `white-space: pre`: both preserve the spaces (measured
        identical), but this one is defined for SVG text specifically — it folds newlines and
        tabs into spaces, which is what a single-line path string wants, where `pre` carries
        line-breaking semantics SVG text has no answer for. It also stays clear of the `style`
        object below, which the comment there asks callers not to crowd.
      */
      xmlSpace="preserve"
      // Typography goes through `style` rather than presentation attributes so `letterSpacing`
      // carries an unambiguous CSS length (SVG's own `letter-spacing` attribute predates CSS units
      // and browsers disagree about a bare number on it).
      style={{ fontFamily, fontWeight, fontStyle, letterSpacing }}
      data-text-on-path="true"
      {...{ [TEXT_ON_PATH_TEXT_NODE_ATTR]: shapeId }}
    >
      <textPath
        href={`#${pathId}`}
        // Safari only picked up SVG2's plain `href` on <textPath> recently; the xlink
        // form is deprecated but still honored everywhere, so send both.
        xlinkHref={`#${pathId}`}
        startOffset={startOffset}
      >
        {/*
          Wire `TO`. Negative lifts the text off the path (SVG y grows downward).

          On a `<tspan>`, NOT on the `<textPath>` above — Blink silently ignores `dy` set directly
          on a `<textPath>` element (measured: identical `getBBox().y` for dy=0, dy=-20 and
          dy="-20px"), so WMPCM-9535's Offset control moved nothing at all while the attribute sat
          there. The same value on a nested `<tspan>` shifts correctly. CSS `baseline-shift` on the
          `<textPath>` also works and is the tidier spec-wise answer, but it inverts the sign and
          is a presentation property competing with the `style` object above, so the `<tspan>` is
          the smaller change. Do not "simplify" this back onto the `<textPath>`.
        */}
        <tspan dy={-offset}>{config.text}</tspan>
      </textPath>
    </text>
  )
}
